import React from "react";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

const pillars = [
  { title: "Voice-first", desc: "Speak, don't swipe. No phones, no menus, no learning curve." },
  { title: "It answers", desc: "Not a cold beep — a warm, responsive reply that guides you." },
  { title: "Feels present", desc: "Your things feel aware, not abandoned to a distant server." },
  { title: "Designed for humans", desc: "Built around how families actually lose and find things." },
  { title: "Quietly ambient", desc: "It sits in the background of your home — until you need it." },
];

const comparison = [
  { them: "Silent Bluetooth dots on a map", us: "A voice interaction with your things" },
  { them: "Unlock. Open app. Find device. Tap.", us: "Just ask. Just listen. Done." },
  { them: "Feels like another piece of tech", us: "Feels like a small, responsive companion" },
];

const WhyVoloTag = () => {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative py-28 md:py-36 bg-white"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <p className="text-xs tracking-[0.2em] uppercase text-blue-600 font-medium">Why VoloTag</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            Not another <span className="italic font-light text-zinc-500">tracker</span>.
            <br className="hidden md:block" /> A responsive little <span className="italic font-light text-zinc-500">friend</span>.
          </h2>
          <p className="mt-5 text-zinc-600 text-lg">
            VoloTag is a different category. It doesn't sit on a map — it lives in your moment.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-5 gap-5">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`pillar-${i}`}
              className="rounded-2xl border border-zinc-200 bg-gradient-to-b from-white to-zinc-50 p-6"
            >
              <div className="font-display text-3xl font-light text-zinc-300">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="mt-4 font-display text-lg font-semibold tracking-tight">{p.title}</h3>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 rounded-3xl border border-zinc-200 overflow-hidden">
          <div className="grid md:grid-cols-2">
            <div className="p-8 md:p-10 bg-zinc-50">
              <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Ordinary trackers</div>
              <ul className="mt-6 space-y-4">
                {comparison.map((c) => (
                  <li key={c.them} className="flex items-start gap-3 text-zinc-600">
                    <X className="w-5 h-5 mt-0.5 text-zinc-400" />
                    <span>{c.them}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-8 md:p-10 bg-black text-white">
              <div className="text-xs tracking-[0.2em] uppercase text-blue-300">VoloTag</div>
              <ul className="mt-6 space-y-4">
                {comparison.map((c) => (
                  <li key={c.us} className="flex items-start gap-3 text-zinc-200">
                    <Check className="w-5 h-5 mt-0.5 text-blue-300" />
                    <span>{c.us}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyVoloTag;
